import type { PreparedBarometerAuction } from '@autobid/ui/types/components/AuctionsData'
import type { QueryKey, UseQueryOptions } from '@tanstack/vue-query'
import type { MaybeRef } from 'vue'
import { useAuctionSocket } from '@autobid/ui/composables/car/sockets/useAuctionSocket'
import { useGetAuctionFetcher } from '@autobid/ui/composables/useGetAuctionFetcher'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, onMounted, unref } from 'vue'

export const singleAuctionQueryKey = {
  all: () => ['auction'] as const,
  byAuctionId: (auctionId: number) => ['auction', auctionId] as const,
}

export type AuctionOptions = UseQueryOptions<PreparedBarometerAuction | null, Error>

export function useAuction(id: MaybeRef<number | undefined>, options?: AuctionOptions) {
  const { locale } = useI18n()
  const queryClient = useQueryClient()
  const { fetchAuction } = useGetAuctionFetcher()
  const { init: initSockets } = useAuctionSocket()

  onMounted(initSockets)

  const { data: auction, ...rest } = useQuery(
    computed(() => {
      const unrefedId = unref(id)

      return singleAuctionQueryKey.byAuctionId(unrefedId || 0)
    }) as unknown as QueryKey,
    async () => {
      const unrefedId = unref(id)

      if (!unrefedId) {
        return null
      }

      return await fetchAuction(unrefedId, locale.value)
    },
    {
      refetchOnWindowFocus: false,
      ...(options || {}),
    },
  )

  const prefetch = async () => {
    const unrefedId = unref(id)

    if (!unrefedId)
      return

    const queryKey = singleAuctionQueryKey.byAuctionId(unrefedId)
    const prefetchedData = queryClient.getQueryData(queryKey)

    if (prefetchedData)
      return prefetchedData

    const data = await fetchAuction(unrefedId, locale.value)

    queryClient.setQueryData(queryKey, data)

    return data
  }

  return {
    auction,
    prefetch,
    ...rest,
  }
}
